import { Modal, Button, Table, InputGroup, Form, Dropdown } from 'react-bootstrap';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Select from 'react-select';
import { Popover } from 'react-tiny-popover';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from 'react-query';
import currency from 'currency.js';
import DateTime from 'react-datetime';
import { IoCheckmarkDoneSharp } from 'react-icons/io5';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { nanoid } from 'nanoid';

import { CustomInput } from '../utils/CustomInput';
import { useAuth } from '../../hooks/useAuth';
import '../../assets/scss/scoped/issueItem.scss';
import ConfirmDialog from '../ConfirmDialogue';
import { fetchActionsUtil } from '../../utils/helpers';
import GetTemplateModal from './GetTemplateModal';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import DotsVeritcalIcon from 'mdi-react/DotsVerticalIcon';
import { DeleteIcon } from '../Icons';
import ModalLoader from '../utils/ModalLoader';
import NewRequisitionModal from '../Requisition/NewRequisition';
import { format } from 'date-fns';
import NewPurchaseRequisitionModal from '../Requisition/NewPurchaseRequisitionModal';

// Custom hook for localStorage with error handling
const useLocalStorage = (key, defaultValue = null) => {
  try {
    const item = window.localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error(`Error reading localStorage key "${key}":`, error);
    return defaultValue;
  }
};

const EditablePlanRow = ({
  planId,
  allPlans,
  plan,
  handleRemove,
  index,
  edit,
  initProps,
  isNewRow = false,
}) => {
  const [showItemsPopover, setShowItemsPopover] = useState(false);
  const { user } = useAuth();
  const plantLocal = useLocalStorage('plantDetails', {});
  const isInitializedRef = useRef(false);
  const previousPlanRef = useRef(plan);
  const DateTimeRef = useRef(null);

  useEffect(() => {
    if (DateTimeRef.current) {
      if (DateTimeRef.current?.style) {
        DateTimeRef.current.style.zIndex = '100';
        DateTimeRef.current.style.position = 'fixed'; // Ensure it is positioned correctly
      }
    }
  }, [DateTimeRef]);

  const initialValues = useMemo(
    () => ({
      Bar_Code: '',
      Item_Name: '',
      Quantity: 0,
      UnitCost: 0,
      Date_Log: format(new Date(), 'yyyy-MM-dd'),
      prod_profiles: [],
      dueDate: new Date(),
    }),
    []
  );

  const formik = useFormik({
    initialValues,
    validationSchema: yup.object().shape({}),
    onSubmit: (values) => {},
  });

  // Memoized setup function to prevent unnecessary re-renders
  const setUp = useCallback(() => {
    if (!isNewRow && plan && plan === previousPlanRef.current) {
      const unitCost = plan.prod_profiles.reduce(
        (acc, cost) => acc + Number(cost.UnitCost) * cost.quantity,
        0
      );
      try {
        const safeValues = {
          Bar_Code: plan.Bar_Code || '',
          Item_Name: plan.Item_Name || '',
          Quantity: Number(plan.Quantity) || 1,
          UnitCost: unitCost || 0,
          Date_Log: format(new Date(), 'yyyy-MM-dd'),
          prod_profiles: Array.isArray(plan.prod_profiles) ? plan.prod_profiles : [],
          dueDate: plan.dueDate ? new Date(plan.dueDate) : new Date(),
        };
        formik.setValues(safeValues);
        previousPlanRef.current = 'nothing';
        isInitializedRef.current = true;
      } catch (error) {
        console.error('Error setting form values:', error);
        toast.error('Failed to load plan data');
      }
    }
  }, [plan, isNewRow, formik.setValues]);

  // Initialize form values only once when plan changes
  useEffect(() => {
    setUp();
  }, [setUp]);

  // Handle form value changes with debouncing to prevent infinite loops
  const handleFormChange = useCallback(() => {
    if (!isNewRow && isInitializedRef.current) {
      const formValues = { ...formik.values };

      // Ensure all required fields exist
      for (let k in initialValues) {
        if (!formValues.hasOwnProperty(k)) {
          formValues[k] = initialValues[k];
        }
      }

      const updatedFormValues = {
        ...formValues,
        UserName: user.username,
        plant: plantLocal.PLID,
        Plan_Id: planId,
      };

      edit({
        index,
        formValues: updatedFormValues,
      });
    }
  }, [
    formik.values,
    isNewRow,
    // index,
    // edit,
    // user.username,
    // plantLocal.PLID,
    // plan?.Plan_Id,
    // initialValues,
  ]);

  // Debounced effect for form changes
  useEffect(() => {
    if (!isNewRow && isInitializedRef.current) {
      const timeoutId = setTimeout(handleFormChange, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [handleFormChange, isNewRow]);

  const handleSelectedItem = useCallback(
    (selectedItem) => {
      const existingPlan = allPlans.findIndex((plan) => plan.Bar_Code === selectedItem.Bar_Code);
      if (existingPlan >= 0 && existingPlan !== index) {
        toast.error('Item already exists in the plan');
        return;
      }
      if (existingPlan >= 0 && existingPlan === index) {
        setShowItemsPopover(false);
        return; // If the item is already in the current row, just update the values
      }
      const newPlanData = {
        ...selectedItem,
        Quantity: 1,
        UserName: user.username,
        plant: plantLocal.PLID,
        Date_Log: format(new Date(), 'yyyy-MM-dd'),
        Plan_Id: planId,
        dueDate: new Date(),
      };

      formik.setValues({
        Bar_Code: selectedItem.Bar_Code,
        Item_Name: selectedItem.Item_Name,
        Quantity: 1,
        UnitCost: selectedItem.UnitCost || 0,
        Date_Log: format(new Date(), 'yyyy-MM-dd'),
        prod_profiles: selectedItem.prod_profiles || [],
        dueDate: selectedItem.dueDate ? new Date(selectedItem.dueDate) : new Date(),
      });
      // Trigger edit to add this as a new plan
      edit({
        index,
        formValues: newPlanData,
        isNewRow: index >= allPlans.length,
      });

      setShowItemsPopover(false);
    },
    [edit, index, formik.setValues, user.username, plantLocal.PLID]
  );

  return (
    <tr>
      <td>
        {!isNewRow && (
          <Dropdown>
            <Dropdown.Toggle variant='' className='bg-white border-0' bsPrefix='print more'>
              <DotsVeritcalIcon />
            </Dropdown.Toggle>
            <Dropdown.Menu
              popperConfig={{
                strategy: 'fixed',
              }}
              renderOnMount
              className='dropdown-with-icons'
            >
              <Dropdown.Item as='button' onClick={() => handleRemove(index)}>
                <DeleteIcon />
                Remove
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </td>
      <td>{index + 1}</td>
      <td>
        <InputGroup className='flex-nowrap'>
          <Form.Control
            name='Bar_Code'
            value={formik.values.Bar_Code}
            onChange={formik.handleChange}
            readOnly={!isNewRow}
            placeholder={isNewRow ? 'Select item...' : ''}
          />
          {/* {isNewRow && ( */}
          <Popover
            isOpen={showItemsPopover}
            onClickOutside={() => setShowItemsPopover(false)}
            content={() => <GetTemplateModal handleSelectedItem={handleSelectedItem} />}
            position='bottom'
          >
            <InputGroup.Text onClick={() => setShowItemsPopover(true)}>
              <MagnifyIcon />
            </InputGroup.Text>
          </Popover>
          {/* )} */}
        </InputGroup>
      </td>
      <td>
        <Form.Control
          name='Item_Name'
          value={formik.values.Item_Name}
          onChange={formik.handleChange}
          readOnly={!isNewRow}
        />
      </td>
      <td>
        <Form.Control
          type='number'
          name='Quantity'
          value={formik.values.Quantity}
          onChange={formik.handleChange}
          min='1'
          disabled={isNewRow && !formik.values.Bar_Code}
        />
      </td>
      <td>
        <Form.Control
          name='UnitCost'
          value={currency(formik.values.UnitCost, { symbol: '' }).format()}
          readOnly
        />
      </td>
      <td>
        <Form.Control readOnly value={plan?.Product_Model || 'N/A'} />
      </td>
      {initProps && <td>{plan.complete ? 'Fulfiled' : 'Unfulfiled'}</td>}
      <td>
        <DateTime
          timeFormat={false}
          closeOnSelect={true}
          closeOnClickOutside={true}
          dateFormat='MMM DD, YYYY'
          name='dueDate'
          ref={DateTimeRef}
          inputProps={{
            style: { zIndex: '100' },
            className: 'date-input form-control',
            placeholder: 'Select date',
            readOnly: true,
          }}
          value={plan.dueDate ? new Date(plan.dueDate) : new Date(formik.values.dueDate)}
          onChange={(date) => formik.setFieldValue('dueDate', date, true)}
          disabled={isNewRow && !formik.values.Bar_Code}
        />
      </td>
    </tr>
  );
};

export const CreateProductionPlanModal = (props) => {
  const [tableData, setTableData] = useState([]);
  const [activeTableIndex, setActiveTableIndex] = useState(null);
  const [selectedItem, setSelectedItem] = useState({});
  const [selectedReceiver, setSelectedReceiver] = useState('');
  const [result, setResult] = useState();
  const [itemsRequested, setItemsRequested] = useState([]);
  const [showNewRequisitionModal, setShowNewRequisitionModal] = useState(false);
  const [showPurchaseRequisitionModal, setShowPurchaseRequisitionModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [allPlans, setAllPlans] = useState(props?.choosedItem || []);
  const [requisitionData, setRequisitionData] = useState([]);
  const previousTableRef = useRef('');

  const { backendUrl, token, user } = useAuth();
  const plantLocal = useLocalStorage('plantDetails', {});

  const [planId] = useState(props?.choosedItem?.length ? props.choosedItem[0]?.Plan_Id : nanoid());

  const initialValues = useMemo(
    () => ({
      Plan_Name: props?.choosedItem?.[0]?.Plan_Name || '',
      Plan_Id: planId,
    }),
    [props?.choosedItem, planId]
  );

  const [isLoadingAddToCart, setIsLoadingAddToCart] = useState(false);

  const fetchItems = async () => {
    const data = await fetchActionsUtil(
      `${backendUrl}/api/production/IssueTo_Officers`,
      'GET',
      token
    );
    data.staffs = data.staffs.map((el) => ({
      value: el.Staff_ID,
      label: el.Name,
    }));

    data.warehouse = [{ value: 'Sales', label: 'Sales' }].concat(
      ...data.warehouse.map((el) => ({
        value: el.W_ID,
        label: el.W_name,
      }))
    );

    return data;
  };

  const { data, isLoading } = useQuery(['GET_OTHERS'], () => fetchItems(), {
    enabled: true,
  });

  const sendProductLog = useMutation(
    (payload) =>
      fetchActionsUtil(`${backendUrl}/api/production/product-plan`, 'POST', token, payload),
    {
      onSuccess: ({ message, data }) => {
        toast.success('Successfully Created');
        formik.resetForm();
        setAllPlans([]);
        setTableData([]);
        props.refetchMain();
        props.onHide();
      },
      onError: () => {
        toast.error(`Unable to perform action`);
      },
    }
  );

  const editProductPlan = useMutation(
    (payload) =>
      fetchActionsUtil(`${backendUrl}/api/production/product-plan`, 'PUT', token, payload),
    {
      onSuccess: ({ message, data }) => {
        toast.success('Successfully Updated');
        props.onHide();
        props.refetchMain();
      },
      onError: () => {
        toast.error(`Unable to perform action`);
      },
    }
  );

  const formik = useFormik({
    initialValues,
    validationSchema: yup.object().shape({
      Plan_Name: yup.string().required('Plan Name is required'),
    }),
    onSubmit: async (values) => {
      if (allPlans.length < 1) {
        return toast.error('Please Add Items to the plan');
      }

      let submittingData = { tableData: allPlans };
      submittingData.tableData.map((ele) => (ele.Plan_Name = values.Plan_Name));

      if (props?.choosedItem) {
        if (
          await ConfirmDialog({
            title: 'Edit Production Plan',
            description: 'Are you sure you want to edit this production plan',
          })
        ) {
          return editProductPlan.mutate(submittingData);
        }
      } else {
        if (
          await ConfirmDialog({
            title: 'Save Production Plan',
            description: 'Are you sure you want to save this production plan',
          })
        ) {
          sendProductLog.mutate(submittingData);
        }
      }
    },
  });

  // Memoized edit function to prevent unnecessary re-renders
  const edit = useCallback(
    ({ index, formValues, isNewRow = false }) => {
      if (isNewRow && formValues.Bar_Code) {
        // Check if item already exists
        const existingPlan = allPlans.find((plan) => plan.Bar_Code === formValues.Bar_Code);
        if (existingPlan) {
          toast.error('Item already exists in the plan');
          return;
        }

        // Add new plan
        setAllPlans((prev) => [...prev, formValues]);
      } else if (!isNewRow) {
        // Update existing plan
        setAllPlans((prev) => {
          const updatedPlans = [...prev];
          updatedPlans[index] = {
            ...updatedPlans[index],
            ...formValues,
          };
          return updatedPlans;
        });
      }
    },
    [allPlans]
  );

  useEffect(() => {
    setErrorMessage(
      tableData.some((item) => Number(item?.item_data?.Quantity || 0) < item?.maxQuantity)
    );
  }, [tableData]);

  // Update tableData whenever allPlans changes
  useEffect(() => {
    const aggregatedProfiles = [];
    allPlans.forEach((plan, planIndex) => {
      if (plan.prod_profiles && plan.prod_profiles.length > 0) {
        plan.prod_profiles.forEach((profile, profileIndex) => {
          const maxQuantity = currency(profile.quantity, { symbol: '' })
            .multiply(plan.Quantity || 1)
            .format();

          aggregatedProfiles.push({
            ...profile,
            planIndex,
            profileIndex,
            planName: plan.Item_Name,
            planBarCode: plan.Bar_Code,
            planQuantity: plan.Quantity,
            maxQuantity,
            blinkRed: Number(profile?.item_data?.Quantity || 0) < maxQuantity,
          });
        });
      }
    });
    setTableData(aggregatedProfiles);
    setErrorMessage(
      allPlans.some((item) => item.nonAvailableItems && item.nonAvailableItems?.length > 0)
    );
  }, [allPlans]);

  // useEffect(() => {
  //   console.log(tableData)
  //   setErrorMessage(
  //     tableData.some((item) => item.nonAvailableItems && item.nonAvailableItems?.length > 0)
  //   );
  // }, [tableData]);

  // Mutation for checking quantity items
  const checkQuantityMutation = useMutation(
    (itemsToCheck) =>
      fetchActionsUtil(
        `${backendUrl}/api/production/check-quantity-items/${plantLocal.PLID}`,
        'POST',
        token,
        { prod_profiles: itemsToCheck }
      ),
    {
      onSuccess: ({ message }) => {
        if (message.length > 0) {
          setItemsRequested(message?.map((er) => er.item));
          // setErrorMessage('Some items required are not enough in stock');
        } else {
          // setErrorMessage(null);
          setItemsRequested([]);
        }
      },
      onError: (error) => {
        console.error('Error checking quantity items:', error);
        // setErrorMessage('Failed to check item quantities');
      },
    }
  );

  useEffect(() => {
    // Compare stringified versions to avoid reference issues
    const prevTableString = JSON.stringify(previousTableRef.current);
    const currTableString = JSON.stringify(tableData);

    if (currTableString !== prevTableString) {
      if (tableData.length < 1) return;
      previousTableRef.current = tableData;
      const itemsToCheck = allPlans
        .map(({ prod_profiles, Quantity }) => {
          const totalQuantity = Number(Quantity);
          return prod_profiles.map((el) => ({
            ...el,
            totalQuantity: totalQuantity * el.quantity,
          }));
        })
        .flat();
      checkQuantityMutation.mutate(itemsToCheck);
    }
  }, [tableData, allPlans, checkQuantityMutation]);

  const handleRemove = useCallback(
    async (index) => {
      const plan = allPlans[index];
      try {
        if (
          await ConfirmDialog({
            title: 'Delete Plan Item',
            description: 'Are you sure you want to delete this item from the plan?',
          })
        ) {
          setAllPlans((prev) => prev.filter((_, i) => i !== index));

          if (plan.Plan_Id && plan.plant) {
            await fetchActionsUtil(
              `${backendUrl}/api/production/product-plan/${plan.plant}/${plan.Plan_Id}/${
                plan.Bar_Code
              }`,
              'DELETE',
              token
            );
          }
        }
      } catch (error) {
        console.error('Error deleting plan:', error);
      }
    },
    [allPlans, backendUrl, token]
  );

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Production Materials Report', 14, 22);
    doc.setFontSize(12);
    doc.text(`Plan: ${formik.values.Plan_Name}`, 14, 32);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 42);

    const tableColumns = [
      'S/N',
      'Plan Item',
      'Material Code',
      'Material Name',
      'UOM',
      'Qty in BOM',
      'Qty Required',
      'Qty Available',
      'Shorfall',
    ];

    const tableRows = tableData.map((item, index) => [
      index + 1,
      item.planName,
      item.item,
      item.title,
      item.Product_Model || '',
      item.quantity,
      item.maxQuantity,
      item?.item_data?.Quantity || 0,
      item.maxQuantity - (item?.item_data?.Quantity || 0) > 0
        ? item.maxQuantity - (item?.item_data?.Quantity || 0)
        : 0,
    ]);

    doc.autoTable({
      head: [tableColumns],
      body: tableRows,
      startY: 50,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save(`production-materials-${formik.values.Plan_Name || 'plan'}.pdf`);
  };

  const exportToExcel = () => {
    const exportData = tableData.map((item, index) => ({
      'S/N': index + 1,
      'Plan Item': item.planName,
      'Material Code': item.item,
      'Material Name': item.title,
      'U of M': item.Product_Model || '',
      'Qty in BOM': item.quantity,
      'Qty Required': item.maxQuantity,
      'Qty Available': item?.item_data?.Quantity || 0,
      'Qty to complete':
        item.maxQuantity - (item?.item_data?.Quantity || 0) > 0
          ? item.maxQuantity - (item?.item_data?.Quantity || 0)
          : 0,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Production Materials');
    XLSX.writeFile(wb, `production-materials-${formik.values.Plan_Name || 'plan'}.xlsx`);
  };

  const handleRequisitionRequest = () => {
    const requestItems = tableData
      .map((el) => {
        return {
          barcode: el.item,
          Item_Name: el?.item_data?.Item_Name,
          quantity: el.planQuantity * el.quantity - (el?.item_data?.Quantity || 0) || 0,
          unitCost: el.UnitCost,
          item_data: el.item_data,
        };
      })
      .filter((el) => el.quantity > 0);
    setRequisitionData(requestItems);
    setShowPurchaseRequisitionModal(true);
  };

  return (
    <Modal
      onHide={() => {
        props.refetchMain();
        props.onHide();
      }}
      fullscreen={true}
      show={props.show}
      backdropClassName={`global-backdrop`}
      aria-labelledby='contained-modal-title-vcenter'
      centered
      dialogClassName='my-style-modal'
      animation={false}
      enforceFocus={false}
      size='full'
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <div>
            <h1>{`${props?.choosedItem ? 'Edit' : 'New'}`} Production Plan</h1>
          </div>
          <p>
            {`${props?.choosedItem ? 'Edit' : 'Create'}`} production plan by adding a BOM to the
            list.
          </p>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* <div className='container'> */}
        <div className='form-relative' id='create-product-plan'>
          {/* Plan Name Form */}
          <div className='row mb-4'>
            <div className='col-12'>
              <CustomInput
                name='Plan_Name'
                placeholder='Enter Plan Name'
                typeOfInput='text'
                label='Plan Title'
                value={formik.values.Plan_Name}
                onChange={formik.handleChange}
              />
              {formik.touched.Plan_Name && !!formik.errors.Plan_Name ? (
                <span className='custom-invalid-feedback'>{formik.errors.Plan_Name}</span>
              ) : null}
            </div>
          </div>

          {/* Production Plan Items Table */}
          <div className='mb-4'>
            <h5 className='mb-3'>Production Plan Items</h5>
            <div className='table-holder'>
              <Table responsive borderless hover striped className='product-table text-nowrap'>
                <thead>
                  <tr>
                    <th />
                    <th>S/N</th>
                    <th>Item Code</th>
                    <th>Item Name</th>
                    <th>Quantity</th>
                    <th>Unit CPU</th>
                    <th>UOM</th>
                    {props.choosedItem && <th>Status</th>}
                    <th>Due Date</th>
                  </tr>
                </thead>
                <tbody key={allPlans?.length}>
                  {allPlans.map((plan, index) => {
                    return (
                      <EditablePlanRow
                        planId={planId}
                        key={`${plan.Plan_Id || plan.Bar_Code}-${index}`}
                        index={index}
                        plan={plan}
                        handleRemove={handleRemove}
                        edit={edit}
                        allPlans={allPlans}
                        initProps={props.choosedItem}
                      />
                    );
                  })}
                  {/* Always show empty row for adding new plans */}
                  <EditablePlanRow
                    planId={planId}
                    key={`new-${allPlans.length}`}
                    index={allPlans.length}
                    plan={{}}
                    handleRemove={handleRemove}
                    edit={edit}
                    isNewRow={true}
                    allPlans={allPlans}
                    initProps={props.choosedItem}
                  />
                </tbody>
              </Table>
            </div>

            {/* {allPlans.length === 0 && (
                <div className='no-item my-4'>
                  <div className='info'>
                    <h2 className='mb-2'>No items added to the plan yet</h2>
                    <p>Use the empty row above to add items to your production plan.</p>
                  </div>
                </div>
              )} */}
          </div>

          <hr />

          {/* Production Materials Table */}
          <div className='mt-4 mb-4'>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <h5 className='text-primary mb-0'>Material Requirements Plan</h5>
              {tableData.length > 0 && (
                <div className='d-flex gap-2'>
                  <button className='btn btn-outline-success btn-sm' onClick={exportToExcel}>
                    Export Excel
                  </button>
                  {/* <button className='btn btn-outline-danger btn-sm' onClick={exportToPDF}>
                      Export PDF
                    </button> */}
                </div>
              )}
            </div>

            <Table responsive borderless striped className='product-table'>
              <thead>
                <tr>
                  <th>S/N</th>
                  <th>Plan Item</th>
                  <th>Material Code</th>
                  <th>Material Name</th>
                  <th>UOM</th>
                  <th>Qty in BOM</th>
                  <th>Qty Required</th>
                  <th>Qty Available</th>
                  <th>Unit cost</th>
                  <th>Shortfall</th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((item, index) => (
                  <tr
                    key={`${item.planIndex}-${item.profileIndex}`}
                    className={`${activeTableIndex === index ? 'active-row' : ''} ${
                      item.blinkRed ? 'blink-red' : ''
                    }`}
                    onClick={() => activeTableIndex !== index && setActiveTableIndex(index)}
                  >
                    <td>{index + 1}</td>
                    <td>{item.planName}</td>
                    <td>{item.item}</td>
                    <td>{item.title}</td>
                    <td>{item.Product_Model || ''}</td>
                    <td>{item.quantity}</td>
                    <td className={item.blinkRed ? 'text-danger fw-bold' : ''}>
                      {item.maxQuantity}
                    </td>
                    <td>{item?.item_data?.Quantity || 0}</td>
                    <td>{item?.UnitCost || 0}</td>
                    <td className={item.blinkRed ? 'text-danger fw-bold' : ''}>
                      {item.blinkRed ? item.maxQuantity - (item?.item_data?.Quantity || 0) : 0}
                    </td>
                  </tr>
                ))}
                {tableData.length === 0 && (
                  <tr>
                    <td colSpan='9' className='text-center text-muted py-4'>
                      No materials to display. Add items to the production plan above.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {errorMessage && (
            <div className='d-flex justify-content-center mb-3'>
              <p className='text-danger'>
                <b>Some items required are not enough in stock</b>
              </p>
            </div>
          )}

          <div className='d-flex gap-2 justify-content-end mt-3'>
            <button
              type='button'
              onClick={formik.handleSubmit}
              className='btn btn-primary'
              disabled={sendProductLog.isLoading || editProductPlan.isLoading}
            >
              {sendProductLog.isLoading || editProductPlan.isLoading ? 'Saving...' : 'Save Plan'}
            </button>

            {errorMessage && (
              <>
                <button
                  className='btn btn-warning mx-2'
                  onClick={() => setShowNewRequisitionModal(true)}
                >
                  Request for Items
                </button>
                <button className='btn btn-info mx-2' onClick={handleRequisitionRequest}>
                  Purchase Requisition
                </button>
              </>
            )}
          </div>
        </div>

        <ModalLoader
          show={
            sendProductLog.isLoading || isLoading || isLoadingAddToCart || editProductPlan.isLoading
          }
        />

        {showNewRequisitionModal && (
          <NewRequisitionModal
            modalTitle='Send for Approval'
            defaultFormValues={{
              title: formik.values.Plan_Name !== '' ? formik.values.Plan_Name : '',
              description: '',
              type: 'Material Request',
            }}
            itemsRequested={itemsRequested}
            setShowNewRequisitionModal={setShowNewRequisitionModal}
          />
        )}

        {showPurchaseRequisitionModal && (
          <NewPurchaseRequisitionModal
            previousFormValues={{
              type: 'Purchase Request',
              title: formik.values.Plan_Name,
              requestItems: requisitionData,
            }}
            itemsRequested={requisitionData}
            setShowNewRequisitionModal={setShowPurchaseRequisitionModal}
          />
        )}
        {/* </div> */}
      </Modal.Body>
    </Modal>
  );
};
